import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {createPortal} from 'react-dom'
import styled from 'styled-components'
import {getViewportBounds} from '../dropdownPositioning'

/**
 * Popover — an anchored floating surface for arbitrary content (a settings panel, a picker).
 *
 * Ported from the SimplifyHR DS Popover and keeps its MUI-shaped controlled API (`open` /
 * `anchorEl` / `onClose` / `anchorOrigin` / `transformOrigin` / `disableAutoFocus`), so the two
 * apps' callers read the same. Unlike `Dropdown`, which owns its open state and renders a menu of
 * items, the caller owns `open` and renders any children.
 *
 * - Portaled to `<body>` and `position: fixed` one step above the modal layer
 *   (`calc(var(--z-modal) + 1)`, like the DS Select list and Dropdown menu), so no overflow or
 *   scroll ancestor clips it and a dialog's backdrop never covers it. It re-anchors when any ancestor scrolls (a capture-phase listener: the app
 *   scrolls inside `main[data-testid=layout-main]`, not the window), on resize, and when its own
 *   size changes, and it is clamped inside the visible viewport, `viewportGutter` rem from its
 *   edge (default 0.5). Its height cap is the `--popover-max-height` custom property, so content
 *   that pins a footer can size against it.
 * - A non-modal `role="dialog"`: name it with `aria-label` or `aria-labelledby`, and describe it
 *   with `aria-describedby` when it carries a message the focused control skips past. A caller
 *   that traps Tab inside its content sets `aria-modal` (STFFLG-4230), which keeps a screen
 *   reader's virtual cursor in the dialog too; the popover itself traps nothing.
 * - `onClose(reason, event)` runs on Escape (`'escapeKeyDown'`), on a mouse press outside the
 *   surface and the anchor (`'outsideClick'`), and when focus lands outside the surface, the
 *   anchor and their portaled children (`'focusOut'`; focus that goes to nothing, such as a
 *   focused button disabling itself, does not count). A press or focus inside a surface portaled
 *   from the popover's own children (a Select list, a Tooltip bubble) counts as inside: React
 *   events reach the popover through the React tree. An Escape that something inside already used
 *   (`defaultPrevented`) is left alone, and the one the popover uses is marked used, so a DS Modal
 *   around it stays open.
 * - It focuses its surface when it opens, the first open included, unless `disableAutoFocus` is
 *   set (for a caller that moves focus itself). It never restores focus on close: that is the
 *   caller's, and a default would reopen a panel that opens on focus.
 *
 * The SimplifyHR original mounted its surface only once its coordinates were known, a commit after
 * `open`, so the focus effect missed the first open. Here the surface mounts with `open` and is
 * placed in a layout effect before the browser paints.
 */

export type PopoverVertical = 'top' | 'center' | 'bottom'
export type PopoverHorizontal = 'left' | 'center' | 'right'
export interface PopoverOrigin {
  vertical: PopoverVertical
  horizontal: PopoverHorizontal
}
/** Why the popover asks to close: Escape, a mouse press outside it and its anchor, or focus
 * landing outside them. */
export type PopoverCloseReason = 'escapeKeyDown' | 'outsideClick' | 'focusOut'

export interface PopoverProps {
  open: boolean
  /** Element to anchor against. `null` renders nothing (as MUI). */
  anchorEl: HTMLElement | null
  onClose: (reason: PopoverCloseReason, event: KeyboardEvent | MouseEvent | FocusEvent) => void
  /** Point ON THE ANCHOR the popover attaches to. Defaults to bottom-left. */
  anchorOrigin?: PopoverOrigin
  /** Point ON THE POPOVER aligned to `anchorOrigin`. Defaults to top-left. */
  transformOrigin?: PopoverOrigin
  children?: ReactNode
  /** Gap between anchor and popover, in rem. Defaults to 0.25 (the `--space-1` step). */
  offset?: number
  className?: string
  style?: CSSProperties
  id?: string
  'data-testid'?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  /** Skip moving focus into the surface on open (MUI's disableAutoFocus). */
  disableAutoFocus?: boolean
  /**
   * Mark the dialog modal (`aria-modal="true"`), so a screen reader's virtual cursor stays inside
   * it. Set it only alongside the caller's own focus trap: the popover does not keep focus inside,
   * and it still closes on a press or focus outside. Unset or false: no attribute.
   */
  'aria-modal'?: boolean
  /**
   * The gap kept between the surface and the viewport edge when it is nudged back on screen, in
   * rem. Defaults to 0.5 (the `--space-2` step). Keep it at 1 or below: the surface's size caps
   * leave 1rem a side (`100vw` / `100vh` minus `--space-8`), so above 1 a surface at its cap keeps
   * the gap on its start edge only.
   */
  viewportGutter?: number
}

const PopoverSurface = styled.div`
  /* The height cap, as a custom property its content can size against (a pinned footer). */
  --popover-max-height: calc(100vh - var(--space-8));

  position: fixed;
  /* Above the modal layer (DS Modal and Drawer = --z-modal, MUI Dialog = 1300), so a popover
     opened inside a dialog isn't painted behind its backdrop. This matches the Select list and
     Dropdown menu. Still below toast/tooltip. */
  z-index: calc(var(--z-modal) + 1);
  box-sizing: border-box;
  max-width: calc(100vw - var(--space-8));
  max-height: var(--popover-max-height);
  overflow: auto;
  background: var(--bg-elevated);
  border: var(--border-thin) solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-elevated);

  /* The surface takes focus only as a starting point for the keyboard; it is not a control. */
  &:focus {
    outline: none;
  }
`

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {vertical: 'bottom', horizontal: 'left'}
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {vertical: 'top', horizontal: 'left'}
/** The `--space-1` step, as the default gap between anchor and surface. */
const DEFAULT_OFFSET_REM = 0.25
/**
 * The `--space-2` step, kept between the surface and the viewport edge when it is nudged in: the
 * `viewportGutter` default.
 */
const VIEWPORT_GUTTER_REM = 0.5
/** CSS `medium`: the root font size where nothing is laid out (jsdom reports none). */
const CSS_MEDIUM_FONT_SIZE = 16

const originFraction = (point: PopoverVertical | PopoverHorizontal): number =>
  point === 'top' || point === 'left' ? 0 : point === 'center' ? 0.5 : 1

/** One rem in px: positions are px, from getBoundingClientRect. */
const remInPx = (): number => {
  const size = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize)
  return size > 0 ? size : CSS_MEDIUM_FONT_SIZE
}

export function Popover({
  open,
  anchorEl,
  onClose,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  children,
  offset = DEFAULT_OFFSET_REM,
  className,
  style,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  disableAutoFocus = false,
  'aria-modal': ariaModal,
  viewportGutter = VIEWPORT_GUTTER_REM,
}: PopoverProps) {
  const surfaceRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<{top: number; left: number} | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // Set by a press, or by focus, anywhere in the popover's React tree, portaled children included.
  const pressedInsideRef = useRef(false)
  const focusedInsideRef = useRef(false)

  const shown = open && anchorEl !== null
  const {vertical: anchorVertical, horizontal: anchorHorizontal} = anchorOrigin
  const {vertical: transformVertical, horizontal: transformHorizontal} = transformOrigin

  // Place the surface, then keep it on its anchor while any ancestor scrolls (capture phase), the
  // window or visual viewport changes, or the surface itself changes size.
  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setCoords(null)
      return
    }
    const update = () => {
      const rect = anchorEl.getBoundingClientRect()
      const rem = remInPx()
      let top = rect.top + rect.height * originFraction(anchorVertical) + offset * rem
      let left = rect.left + rect.width * originFraction(anchorHorizontal)

      // Keep the surface on screen. offsetWidth/Height, not a client rect: the surface carries the
      // transformOrigin translate, and a client rect would report the moved box.
      const surface = surfaceRef.current
      if (surface && surface.offsetWidth > 0) {
        const viewport = getViewportBounds()
        const gutter = viewportGutter * rem
        const width = surface.offsetWidth
        const height = surface.offsetHeight
        const boxLeft = left - originFraction(transformHorizontal) * width
        const boxTop = top - originFraction(transformVertical) * height
        // Clamping the start after the end keeps the top-left reachable when the surface is larger
        // than the viewport.
        const fit = (start: number, size: number, min: number, max: number) =>
          Math.max(min + gutter, Math.min(start, max - gutter - size))
        left += fit(boxLeft, width, viewport.left, viewport.right) - boxLeft
        top += fit(boxTop, height, viewport.top, viewport.bottom) - boxTop
      }

      // Identity-stable, so an update with nothing moved does not re-render.
      setCoords((prev) => (prev && prev.top === top && prev.left === left ? prev : {top, left}))
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    window.visualViewport?.addEventListener('scroll', update)
    window.visualViewport?.addEventListener('resize', update)
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    if (surfaceRef.current) resizeObserver?.observe(surfaceRef.current)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('scroll', update)
      window.visualViewport?.removeEventListener('resize', update)
      resizeObserver?.disconnect()
    }
  }, [
    open,
    anchorEl,
    anchorVertical,
    anchorHorizontal,
    transformVertical,
    transformHorizontal,
    offset,
    viewportGutter,
  ])

  // Escape, a press outside and focus landing outside close it. The capture-phase resets run
  // before React's handlers, so an event whose propagation something stopped can't leave an inside
  // flag set. Focus that goes to nothing fires no focusin, so it never closes the popover.
  useEffect(() => {
    if (!open || !anchorEl) return
    const resetPress = () => {
      pressedInsideRef.current = false
    }
    const resetFocus = () => {
      focusedInsideRef.current = false
    }
    const onMouseDown = (event: MouseEvent) => {
      const pressedInside = pressedInsideRef.current
      pressedInsideRef.current = false
      const target = event.target
      if (pressedInside) return
      if (
        target instanceof Node &&
        (surfaceRef.current?.contains(target) || anchorEl.contains(target))
      )
        return
      onCloseRef.current('outsideClick', event)
    }
    const onFocusIn = (event: FocusEvent) => {
      const focusedInside = focusedInsideRef.current
      focusedInsideRef.current = false
      if (focusedInside) return
      const target = event.target
      if (
        target instanceof Node &&
        (surfaceRef.current?.contains(target) || anchorEl.contains(target))
      )
        return
      onCloseRef.current('focusOut', event)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      event.preventDefault()
      onCloseRef.current('escapeKeyDown', event)
    }
    document.addEventListener('mousedown', resetPress, true)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('focusin', resetFocus, true)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', resetPress, true)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('focusin', resetFocus, true)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, anchorEl])

  // The surface mounts in the same commit as `open`, so this runs on the first open too.
  useEffect(() => {
    if (!shown || disableAutoFocus) return
    surfaceRef.current?.focus({preventScroll: true})
  }, [shown, disableAutoFocus])

  if (!shown) return null

  // Escape with focus inside reaches this through React before any document listener, so the
  // popover closes alone even inside a Modal that listens on the document.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return
    event.preventDefault()
    onCloseRef.current('escapeKeyDown', event.nativeEvent)
  }
  const handleMouseDownCapture = () => {
    pressedInsideRef.current = true
  }
  // React's focusin handler runs on the portal container, before the document's bubble listener.
  const handleFocusCapture = () => {
    focusedInsideRef.current = true
  }

  // transformOrigin moves the surface by a fraction of its own size, in CSS.
  const translateX = -originFraction(transformHorizontal) * 100
  const translateY = -originFraction(transformVertical) * 100

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the dialog listens for Escape and for presses and focus inside its own React tree; it is not a control
    <PopoverSurface
      ref={surfaceRef}
      role="dialog"
      tabIndex={-1}
      id={id}
      className={className}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-modal={ariaModal || undefined}
      data-testid={dataTestId}
      onKeyDown={handleKeyDown}
      onMouseDownCapture={handleMouseDownCapture}
      onFocusCapture={handleFocusCapture}
      style={{
        top: coords?.top ?? 0,
        left: coords?.left ?? 0,
        transform: `translate(${translateX}%, ${translateY}%)`,
        ...style,
      }}>
      {children}
    </PopoverSurface>,
    document.body,
  )
}
